import type { Coverage } from "@/src/models/coverage";

type TprCasePolicy = {
  insurancePackageId: number;
  customerPayerIds: readonly string[];
  payerPattern: RegExp;
  memberIdPattern?: RegExp;
  formatMemberId?: (memberId: string) => string | null;
};

/** Salt River CHS stays ahead of River People so purchased/referred care does not land on the clinic package. */
const TPR_CASE_POLICIES: readonly TprCasePolicy[] = [
  {
    insurancePackageId: 290666,
    customerPayerIds: ["VACCN", "TWVACCN"],
    payerPattern:
      /optum\s+va\s+community\s+care\s+network|veterans\s+affairs\s+community\s+care\s+network|triwest.*va\s+ccn/i,
  },
  {
    insurancePackageId: 261545,
    customerPayerIds: [],
    payerPattern: /river\s+people\s+health\s+care.*purchased\s+referred\s+care|salt\s+river\s+chs/i,
  },
  {
    insurancePackageId: 3065016,
    customerPayerIds: [],
    payerPattern: /river\s+people\s+health\s+center|\brphc\b/i,
  },
  {
    insurancePackageId: 192976,
    customerPayerIds: ["PIMC"],
    payerPattern: /phoenix\s+indian|\bpimc\b/i,
    formatMemberId: (memberId) => (/^\d+$/.test(memberId) ? `PIMC${memberId}` : null),
  },
  {
    insurancePackageId: 151414,
    customerPayerIds: ["PARKER", "PAH"],
    payerPattern: /\bparker\b|\bpah\b/i,
    memberIdPattern: /^PAH\s*\d+$/i,
    formatMemberId: (memberId) => memberId.replace(/\s+/g, "").toUpperCase(),
  },
  {
    insurancePackageId: 3099372,
    customerPayerIds: ["TONHC"],
    payerPattern: /tohono\s+o['\s]?odham|san\s+xavier|sells\s+indian|\btonhc\b/i,
  },
  {
    insurancePackageId: 47269,
    customerPayerIds: ["GRHC"],
    payerPattern: /gila\s+river|\bgrhc\b/i,
  },
  {
    insurancePackageId: 367549,
    customerPayerIds: ["00290"],
    payerPattern: /fiscal\s+intermediary|bcbs.*nm.*ihs|ihs.*(?:chs|prc).*fi/i,
  },
  {
    insurancePackageId: 93935,
    customerPayerIds: [],
    payerPattern: /whiteriver\s+indian/i,
  },
  {
    insurancePackageId: 643235,
    customerPayerIds: [],
    payerPattern: /chinle\s+ihs/i,
  },
  {
    insurancePackageId: 139928,
    customerPayerIds: [],
    payerPattern: /tuba\s+city/i,
  },
  {
    insurancePackageId: 666974,
    customerPayerIds: [],
    payerPattern: /kayenta\s+indian/i,
  },
  {
    insurancePackageId: 161649,
    customerPayerIds: [],
    payerPattern: /pinon.*(?:contract|health|ihs)/i,
  },
  {
    insurancePackageId: 394257,
    customerPayerIds: [],
    payerPattern: /fort\s+defiance/i,
  },
  {
    insurancePackageId: 58830,
    customerPayerIds: [],
    payerPattern: /hopi\s+health/i,
  },
  {
    insurancePackageId: 258440,
    customerPayerIds: [],
    payerPattern: /pascua\s+yaqui|\byoeme\b/i,
  },
];

function coverageText(coverage: Coverage): string {
  return [coverage.payer, coverage.plan, coverage.customerPayer]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ");
}

function matchesPayer(policy: TprCasePolicy, coverage: Coverage): boolean {
  const customerPayer = coverage.customerPayer?.trim().toUpperCase();

  return (
    (customerPayer !== undefined && policy.customerPayerIds.includes(customerPayer)) ||
    policy.payerPattern.test(coverageText(coverage))
  );
}

function matchesPolicy(policy: TprCasePolicy, coverage: Coverage, memberId: string): boolean {
  return matchesPayer(policy, coverage) || (policy.memberIdPattern?.test(memberId) ?? false);
}

export function resolveTprCasePolicyPackageId(coverage: Coverage): number | null {
  return TPR_CASE_POLICIES.find((policy) => matchesPayer(policy, coverage))?.insurancePackageId ?? null;
}

export function formatTprCasePolicyMemberId(coverage: Coverage, memberId: string): string | null {
  for (const policy of TPR_CASE_POLICIES) {
    if (!policy.formatMemberId || !matchesPolicy(policy, coverage, memberId)) continue;

    const formatted = policy.formatMemberId(memberId);

    if (formatted !== null) return formatted;
  }

  return null;
}
